import FlexibleContentTile from '../../../Shared/Tiles/FlexibleContentTile';
import { Spinner } from '../../Spinner';
import { useEffect, useMemo, useState } from 'react';
import { Table, DataTable, TableCell, TableRow, TableHeader, TableBody } from '@lmig/lmds-react-table';
import { Pagination } from '@lmig/lmds-react-pagination';
import { Button } from '@lmig/lmds-react-button';
import { Link } from '@lmig/lmds-react-link';
import { Tooltip } from '@lmig/lmds-react-tooltip';
import { IconWarning, IconErrorAlert } from '@lmig/lmds-react-icons';
import { getPLPSTLink, getPLSecureUploadLink } from 'Foundation/Configurations/GlobalConfiguration';
import { isSmallMobileView } from 'Utilities/AppHelper';
import { useUserData } from 'Foundation/User/UserContext';
import { StringHelper } from 'Utilities/StringHelper';
import { getAntiForgeryToken } from 'Utilities/AntiForgeryToken';
import './Underwriting.scss';
import RecentActivitiesNoData, { INoDataTileMessage } from 'Feature/PageContent/Shared/Tiles/NoDataTile';


enum SortDirection {
	ascending = 'ascending',
	descending = 'descending',
	none = 'none',
}

enum UnderwritingSortableKeys {
	customer = 'customer',
	dueDate = 'dueDate',
}

interface SortDirections {
	[UnderwritingSortableKeys.customer]: SortDirection;
	[UnderwritingSortableKeys.dueDate]: SortDirection;
}

// type UnderwritingIcon = 'auto' | 'motorcycle';
// type DueState = 'critical' | 'warning';

export type NameValuePair = {
	name: string;
	value: string | string[] | boolean;
};

export type DocsSearchResultItem = {
	contentId: string;
	metadata: NameValuePair[];
	renditions: null;
};

export type DocsSearchResult = {
	items: DocsSearchResultItem[];
};

export interface IUnderwritingTableData {
	id: string;
	customer: string;
	primaryKey: string;
	dueDate: string;
}

interface UnderwritingProps {
	activeTab?: number;
	isLoading?: boolean;
	data?: IUnderwritingTableData[];
	onActionClick?: (row: IUnderwritingTableData) => void;
}

const getRecord = (value: unknown): Record<string, unknown> | null => {
	if (value && typeof value === 'object' && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}

	return null;
};

// The documents API returns PascalCase keys (Items, ContentId, Metadata, Name, Value),
// so look properties up case-insensitively to reliably assign the received values.
const getPropertyValue = (record: Record<string, unknown>, key: string): unknown => {
	if (key in record) {
		return record[key];
	}

	const lowerKey = key.toLowerCase();
	const matchingKey = Object.keys(record).find((recordKey) => recordKey.toLowerCase() === lowerKey);
	return matchingKey === undefined ? undefined : record[matchingKey];
};

const getDocsSearchItems = (payload: unknown): DocsSearchResultItem[] => {
	const record = getRecord(payload);
	const items = record ? getPropertyValue(record, 'items') : null;
	if (!Array.isArray(items)) {
		return [];
	}

	return items
		.map((item) => {
			const itemRecord = getRecord(item);
			if (!itemRecord) {
				return null;
			}

			const contentId = getPropertyValue(itemRecord, 'contentId');
			const metadataValue = getPropertyValue(itemRecord, 'metadata');
			if (typeof contentId !== 'string' || !Array.isArray(metadataValue)) {
				return null;
			}

			const metadata = metadataValue
				.map((pair) => {
					const pairRecord = getRecord(pair);
					if (!pairRecord) {
						return null;
					}

					const name = getPropertyValue(pairRecord, 'name');
					if (typeof name !== 'string') {
						return null;
					}

					const value = getPropertyValue(pairRecord, 'value');
					if (typeof value === 'string' || typeof value === 'boolean' || Array.isArray(value)) {
						return {
							name,
							value,
						} as NameValuePair;
					}

					return null;
				})
				.filter((pair): pair is NameValuePair => Boolean(pair));

			return {
				contentId,
				metadata,
				renditions: null,
			} as DocsSearchResultItem;
		})
		.filter((item): item is DocsSearchResultItem => Boolean(item));
};

const getMetadataStringValue = (metadata: NameValuePair[], key: string) => {
	const match = metadata.find((pair) => pair.name.toLowerCase() === key.toLowerCase());
	if (!match) {
		return '';
	}

	if (typeof match.value === 'string') {
		const value = match.value.trim();
		if (value.length > 0) {
			return value;
		}
	}

	if (Array.isArray(match.value)) {
		const value = match.value.find((item) => typeof item === 'string' && item.trim().length > 0);
		if (typeof value === 'string') {
			return value.trim();
		}
	}

	return '';
};

const formatCustomerName = (value: string): string => {
	if (!StringHelper.hasNonEmptyString(value)) {
		return '';
	}

	return value
		.toLowerCase()
		.split(' ')
		.map((word) => (word.length > 0 ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word))
		.join(' ');
};

const mapUnderwritingData = (payload: unknown): IUnderwritingTableData[] => {
	const rows = getDocsSearchItems(payload);

	return rows
		.map((row, index) => {
			const metadata = row.metadata;

			return {
				id: row.contentId || `row-${index}`,
				customer: formatCustomerName(getMetadataStringValue(metadata, 'customerName')),
				primaryKey: getMetadataStringValue(metadata, 'primaryKey'),
				dueDate: getMetadataStringValue(metadata, 'dueDate'),
			} as IUnderwritingTableData;
		})
		.filter((row): row is IUnderwritingTableData => Boolean(row) && row.customer.length > 0 && row.primaryKey.length > 0);
};

const UNDERWRITING_DOCUMENTS_URL = 'api/sitecore/RecentActivitiesPL/GetRecentPLUnderwritingDocuments';

// The underwriting endpoint expects the agency number hyphenated by length: 6 -> XX-XXXX,
// 8 -> XX-XX-XXXX (delegated to StringHelper.formatStatCode); any other length is sent unchanged.
const formatAgencyNumber = (agencyNumber: string): string => {
	if (agencyNumber.length === 6) {
		return `${agencyNumber.slice(0, 2)}-${agencyNumber.slice(2)}`;
	}
	return StringHelper.formatStatCode(agencyNumber);
};

const fetchUnderwritingDocuments = async (agencyNumber: string, signal?: AbortSignal): Promise<unknown> => {
	// The endpoint validates an anti-forgery token ([ValidateJsonAntiForgeryToken]), so the request
	// must carry the __RequestVerificationToken form field alongside the anti-forgery cookie.
	const requestVerificationToken = await getAntiForgeryToken();
	if (signal?.aborted) {
		throw new DOMException('The underwriting documents request was aborted.', 'AbortError');
	}

	if (!requestVerificationToken) {
		throw new Error('Unable to initialize a secure underwriting documents request.');
	}

	const response = await fetch(UNDERWRITING_DOCUMENTS_URL, {
		method: 'POST',
		signal,
		body: new URLSearchParams({
			__RequestVerificationToken: requestVerificationToken,
			agencyNumber,
		}),
	});
	return response.json();
};

const getSortValue = (value: string) => value.toLowerCase();

// Rows with a blank/unparsable dueDate always sort to the end, regardless of direction.
// Among rows with a valid dueDate, ascending orders soonest to latest and descending orders
// latest to soonest.
const sortByDueDate = (rows: IUnderwritingTableData[], direction: SortDirection): IUnderwritingTableData[] => {
	const getTime = (row: IUnderwritingTableData): number | null => {
		if (!StringHelper.hasNonEmptyString(row.dueDate)) {
			return null;
		}

		const time = new Date(row.dueDate).getTime();
		return Number.isNaN(time) ? null : time;
	};

	return [...rows].sort((a, b) => {
		const timeA = getTime(a);
		const timeB = getTime(b);

		if (timeA === null && timeB === null) {
			return 0;
		}

		if (timeA === null) {
			return 1;
		}

		if (timeB === null) {
			return -1;
		}

		return direction === SortDirection.descending ? timeB - timeA : timeA - timeB;
	});
};

const sortRows = (
	rows: IUnderwritingTableData[],
	key: UnderwritingSortableKeys,
	direction: SortDirection,
): IUnderwritingTableData[] => {
	if (direction === SortDirection.none) {
		return rows;
	}

	if (key === UnderwritingSortableKeys.dueDate) {
		return sortByDueDate(rows, direction);
	}

	const sorted = [...rows].sort((a, b) => getSortValue(a[key]).localeCompare(getSortValue(b[key])));
	return direction === SortDirection.descending ? sorted.reverse() : sorted;
};

// const UnderwritingIconCell = ({ icon }: { icon: UnderwritingIcon }) => {
// 	return <IconAuto size="16" />;
// };

type DueDateSeverity = 'critical' | 'warning' | null;

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// The number of calendar days between today and the due date (negative once the due date has
// passed), or null when the due date is missing/unparsable. Calendar-day based (not a 24-hour
// interval), so it is unaffected by the time-of-day portion of the timestamp.
const getDueDateDiffDays = (dueDateStr: string): number | null => {
	if (!StringHelper.hasNonEmptyString(dueDateStr)) {
		return null;
	}

	const dueDate = new Date(dueDateStr);
	if (Number.isNaN(dueDate.getTime())) {
		return null;
	}

	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const due = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
	return Math.round((due.getTime() - today.getTime()) / MS_PER_DAY);
};

// A red icon appears for items whose due date has already passed or is 0-4 days in the future,
// a yellow icon for items whose due date is 5-10 days in the future, and no icon otherwise
// (including when the API returns no due date, or the due date is further than 10 days out).
export const getDueDateSeverity = (dueDateStr: string): DueDateSeverity => {
	const diffDays = getDueDateDiffDays(dueDateStr);
	if (diffDays === null) {
		return null;
	}

	if (diffDays <= 4) {
		return 'critical';
	}

	if (diffDays <= 10) {
		return 'warning';
	}

	return null;
};

// Per the acceptance criteria, the component only displays action items due 0-14 days from now;
// missing or blank due dates are excluded. Populated but unparsable dates retain their existing
// behavior, since there is no date to evaluate against the window.
export const isDueDateWithinDisplayWindow = (dueDateStr?: string | null): boolean => {
	if (typeof dueDateStr !== 'string' || dueDateStr.trim().length === 0) {
		return false;
	}

	const diffDays = getDueDateDiffDays(dueDateStr);
	return diffDays === null || (diffDays >= 0 && diffDays <= 14);
};


const DueDateIcon = ({ severity }: { severity: DueDateSeverity }) => {
	if (severity === 'critical') {
		return <IconErrorAlert size="16" color="negative" aria-label="Overdue or due within 4 days" className="underwriting-due-icon critical" />;
	}

	if (severity === 'warning') {
		return <IconWarning size="16" color="caution" aria-label="Due within 5 to 10 days" className="underwriting-due-icon warning" />;
	}

	return null;
};

// The API returns dueDate as an ISO 8601 timestamp (e.g. '2026-07-30T16:50:19.403Z'); the UI
// displays it as MM/DD/YYYY. Returns '' (blank cell) when the date is missing or unparsable.
const formatDueDate = (dueDateStr: string): string => {
	if (!StringHelper.hasNonEmptyString(dueDateStr)) {
		return '';
	}

	const date = new Date(dueDateStr);
	if (Number.isNaN(date.getTime())) {
		return '';
	}

	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${month}/${day}/${date.getFullYear()}`;
};

export default function Underwriting(props: UnderwritingProps) {
	const { userData } = useUserData();
	const [isLoading, setIsLoading] = useState<boolean>(props.isLoading ?? true);
	const [isNoExperienceLoading, setIsNoExperienceLoading] = useState<boolean>(true);
	const [apiData, setApiData] = useState<IUnderwritingTableData[]>(props.data ?? []);
	const [isMobileLayout, setIsMobileLayout] = useState<boolean>(isSmallMobileView());
	const [richText, setRichText] = useState<INoDataTileMessage>({
		leftText: '<div>No insights right now.</div>',
		rightText: '<div>No insights right now.</div>',
	});
	// Per the acceptance criteria, the table defaults to sorting by Due date, soonest to latest.
	const [sortDirections, setSortDirections] = useState<SortDirections>({
		[UnderwritingSortableKeys.customer]: SortDirection.none,
		[UnderwritingSortableKeys.dueDate]: SortDirection.ascending,
	});
	const [page, setPage] = useState(0);

	useEffect(() => {
		const handleResize = () => {
			setIsMobileLayout(isSmallMobileView());
		};
		const getRichText = async () => {
			fetch('api/sitecore/PageContentAsync/GetRecentActivitiesTabRichText', {
				method: 'POST',
				body: new URLSearchParams({ type: 'underwriting' }),
			})
				.then((response) => response.json())
				.then((response) => setRichText(response as INoDataTileMessage))
				.catch(() => {
					setRichText({
						leftText: '<div>No insights right now.</div>',
						rightText: '<div>No insights right now.</div>',
					});
				})
				.finally(() => setIsNoExperienceLoading(false));
		};

		handleResize();
		window.addEventListener('resize', handleResize);
		getRichText();

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (props.data) {
			setApiData(props.data);
			setIsLoading(Boolean(props.isLoading));
			return;
		}

		if (!userData) {
			return;
		}

		const agencyNumber = (userData.selectedStatCodeData?.FormattedSubStat ?? '').trim();
		if (!StringHelper.hasNonEmptyString(agencyNumber)) {
			setApiData([]);
			setIsLoading(false);
			return;
		}

		// Abort the in-flight request when the selected stat code changes (or the component
		// unmounts) so a slower response for a previous agency can never overwrite the current
		// agency's rows. Stale completions are also ignored via the aborted-signal checks.
		const controller = new AbortController();

		setIsLoading(true);
		fetchUnderwritingDocuments(formatAgencyNumber(agencyNumber), controller.signal)
			.then((payload) => {
				if (!controller.signal.aborted) {
					setApiData(mapUnderwritingData(payload));
				}
			})
			.catch(() => {
				if (!controller.signal.aborted) {
					setApiData([]);
				}
			})
			.finally(() => {
				if (!controller.signal.aborted) {
					setIsLoading(false);
				}
			});

		return () => controller.abort();
	}, [props.data, props.isLoading, userData]);

	useEffect(() => {
		setPage(0);
	}, [apiData]);

	const tableData = props.data ?? apiData;
	const itemsPerPage = 4;

	// Exclude missing/blank due dates and dates outside the 0-14-day window before sorting
	// and pagination, for both API data and data supplied through props.
	const visibleData = useMemo(
		() => tableData.filter((row) => isDueDateWithinDisplayWindow(row.dueDate)),
		[tableData],
	);

	const selectedSortKey =
		(Object.keys(sortDirections).find(
			(key) => sortDirections[key as UnderwritingSortableKeys] !== SortDirection.none,
		) as UnderwritingSortableKeys) ?? UnderwritingSortableKeys.customer;

	const selectedSortDirection = sortDirections[selectedSortKey];

	const sortedData = useMemo(
		() => sortRows(visibleData, selectedSortKey, selectedSortDirection),
		[visibleData, selectedSortDirection, selectedSortKey],
	);

	const paginatedData = useMemo(() => {
		const start = page * itemsPerPage;
		return sortedData.slice(start, Math.min(start + itemsPerPage, sortedData.length));
	}, [page, sortedData]);

	const tableSorter = (key: UnderwritingSortableKeys) => {
		const getNone = {
			customer: SortDirection.none,
			dueDate: SortDirection.none,
		};

		setSortDirections((prev) => ({
			...getNone,
			[key]: prev[key] === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending,
		}));
		setPage(0);
	};

	const onActionClick = (row: IUnderwritingTableData) => {
		window.open(
			getPLSecureUploadLink() + '/?category=policy2&policy2=' + encodeURIComponent(row.primaryKey),
			'_blank',
			'noopener,noreferrer',
		);

		if (props.onActionClick) {
			props.onActionClick(row);
		}
	};

	const LMDSPagination = () => {
		if (sortedData.length <= itemsPerPage) {
			return <></>;
		}

		return (
			<Pagination
				id="underwriting-pagination"
				className="recent-activities-pagination"
				withWords={false}
				itemCount={sortedData.length}
				onChange={(e: any) => setPage(e.page)}
				showItemsPerPage={false}
				page={page}
				itemsPerPage={itemsPerPage}
			/>
		);
	};

	return isLoading ? (
		<Spinner />
	) : sortedData.length === 0 ? (
		<RecentActivitiesNoData
			messageLeft={richText.leftText}
			messageRight={richText.rightText}
		></RecentActivitiesNoData>
	) : (
		<FlexibleContentTile
			data-cs-mask // IMPORTANT: DO NOT REMOVE data-cs-mask attribute, this ensures the PII data is masked correctly for session replay
			tileClass="underwriting-tile"
			children={[
				<div id="underwriting-table-container" className="recent-activities-table-container">
					<div key={props.activeTab} className="underwriting-tab-content tab-content">
						{isMobileLayout ? (
							<div className="underwriting-mobile-list">
								{paginatedData.map((row, index) => (
									<div key={row.id}>
										<div className="underwriting-mobile-card">
											<div className="underwriting-mobile-section">
												<div className="underwriting-mobile-label">Customer</div>
												<div className="underwriting-mobile-customer-container customer-name-container underwriting-customer-name">
													<Tooltip
														className="recent-activities-tile-tooltip"
														content={
															<Link target="_blank" href={getPLPSTLink() + '?Policy=' + encodeURIComponent(row.primaryKey)}>
																{row.customer}
															</Link>
														}
													>
														<div className="underwriting-mobile-customer-value customer-name">
															<Link target="_blank" href={getPLPSTLink() + '?Policy=' + encodeURIComponent(row.primaryKey)}>
																{row.customer}
															</Link>
														</div>
													</Tooltip>
												</div>
											</div>
											<div className="underwriting-mobile-section">
												<div className="underwriting-mobile-label">Requested</div>
												<div className="underwriting-mobile-requested-value">Underinsured/Uninsured Motorist</div>
											</div>
										<div className="underwriting-mobile-section">
											<div className="underwriting-mobile-label">Actions</div>
												<div className="underwriting-mobile-actions-container">
													<Button
														type="button"
														variant="secondary"
														size="small"
														className="underwriting-submit-button underwriting-mobile-submit-button underwriting-submit-padding"
														onClick={() => onActionClick(row)}
													>
														Submit form
													</Button>
												</div>
											</div>
										<div className="underwriting-mobile-section">
											<div className="underwriting-mobile-label">Due date</div>
											<div className="underwriting-due-column">
												<DueDateIcon severity={getDueDateSeverity(row.dueDate)} />
												<span>{formatDueDate(row.dueDate)}</span>
											</div>
										</div>
										</div>
										{index < paginatedData.length - 1 ? <div className="underwriting-mobile-separator" /> : <></>}
									</div>
								))}
							</div>
						) : (
							<div className="design-system-data-table-container">
								<div className="design-system-data-table">
									<DataTable>
										<Table className="design-system-table" spacing="micro">
											<TableHeader id="underwriting-table-header" className="recent-activities-table-header">
												<TableRow>
													<TableCell
														type="colHead"
														isSortable
														sortDirection={sortDirections.customer}
														onClick={() => tableSorter(UnderwritingSortableKeys.customer)}
														className="underwriting-customer-column-header underwriting-header-cell"
													>
														Customer
													</TableCell>
													<TableCell type="colHead" className="underwriting-requested-column-header underwriting-header-cell">
														Requested
													</TableCell>
													<TableCell type="colHead" className="underwriting-actions-column-header underwriting-header-cell">
														Actions
													</TableCell>
													<TableCell
														type="colHead"
														isSortable
														sortDirection={sortDirections.dueDate}
														onClick={() => tableSorter(UnderwritingSortableKeys.dueDate)}
														className="underwriting-duedate-column-header underwriting-header-cell"
													>
														Due date
													</TableCell>
												</TableRow>
											</TableHeader>
											<TableBody>
												{paginatedData.map((row) => (
													<TableRow key={row.id}>
														<TableCell className="underwriting-customer-cell">
															<div className="customer-name-container underwriting-customer-name">
																{/* <div className="underwriting-customer-icon">
																	<UnderwritingIconCell icon={row.icon} />
																</div> */}
																<Tooltip
																	className="recent-activities-tile-tooltip"
																	content={
																		<Link target="_blank" href={getPLPSTLink() + '?Policy=' + encodeURIComponent(row.primaryKey)}>
																			{row.customer}
																		</Link>
																	}
																>
																	<div className="customer-name">
																		<Link target="_blank" href={getPLPSTLink() + '?Policy=' + encodeURIComponent(row.primaryKey)}>
																			{row.customer}
																		</Link>
																	</div>
																</Tooltip>
															</div>
														</TableCell>
														<TableCell>
															<div className="underwriting-requested-column">{'Underinsured/Uninsured Motorist'}</div>
														</TableCell>
														<TableCell>
															<div className="underwriting-actions-column">
																<Button
																	type="button"
																	variant="secondary"
																	size="small"
																	className="underwriting-submit-button underwriting-submit-padding"
																	onClick={() => onActionClick(row)}
																>
																	{'Submit form'}
																</Button>
															</div>
														</TableCell>
														<TableCell>
															<div className="underwriting-due-column">
																<DueDateIcon severity={getDueDateSeverity(row.dueDate)} />
																<span>{formatDueDate(row.dueDate)}</span>
															</div>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</DataTable>
								</div>
							</div>
						)}
					</div>
					<LMDSPagination />
				</div>,
			]}
		/>
	);
}
